import { Channel, useUpdateChannelMutation } from 'api/ChannelsAPI';
import { InputField, RadioListField } from 'lib/iterable-hook-form';
import { BINDING_TYPE, CHANNEL_FORM_FIELDS_TEXT, CHANNEL_NAME } from 'modules/channels/constants';
import { UpdateChannelFormContext, useChannelsContext } from 'modules/channels/contexts';
import { BindingTypes, UpdateChannelFormState } from 'modules/channels/types';
import { convertToEspBindings, createChannelBindingOptions } from 'modules/channels/utils';
import { MessageMedium } from 'modules/graphql/types';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { ApiError } from 'services/api';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { Help } from 'ui/icons';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { StyledLabel, StyledToggleTip } from '../Styled';
import { SendingPlatformAccounts } from './SendingPlatformAccounts';

export const SENDING_PLATFORMS_TEXT = {
  LABEL: 'Sending platforms',
  TOOLTIP_CONTENT: 'Send your campaigns from one or more sending platforms',
};

const SplitInputLabel = () => (
  <StyledLabel>
    {SENDING_PLATFORMS_TEXT.LABEL}
    <StyledToggleTip tooltipContent={SENDING_PLATFORMS_TEXT.TOOLTIP_CONTENT}>
      <Help />
    </StyledToggleTip>
  </StyledLabel>
);

export const UPDATE_CHANNEL_TEXT = {
  ERROR_MESSAGE: 'Unable to update subscription channel.',
  LABEL_TOOLTIP_CONTENT: 'Send your campaigns from one or more sending platforms',
  MODAL_ID: 'update-channel-modal',
  SAVE_BUTTON_LABEL: 'Save channel',
  SAVE_BUTTON_ID: 'update-channel-btn',
  SUCCESS_MESSAGE: 'Success! Updated:',
};

const PositiveToastMessage = ({ channelName }: { channelName: string }) => (
  <>
    {UPDATE_CHANNEL_TEXT.SUCCESS_MESSAGE} <strong>{channelName}</strong>
  </>
);

type UpdateModalProps = {
  channel: Channel;
  onClose: () => void;
};

export const UpdateChannel = ({ channel, onClose }: UpdateModalProps) => {
  const dispatch = useDispatch();
  const { espAccounts = [], refetchChannels } = useChannelsContext();
  const { channelType, id, messageMedium } = channel;

  const {
    getValues,
    handleSubmit,
    formState: { errors, isSubmitted, isValid },
    watch,
  } = useFormContext<UpdateChannelFormState>();

  const { bindingType: bindingTypeError, channelName: channelNameError } = errors;

  const bindingTypeValue = watch('bindingType');
  const isEmail = messageMedium === MessageMedium.Email;
  const isCustom = bindingTypeValue === BindingTypes.CUSTOM;
  const defaultAccount = espAccounts?.find((account) => account.isDefault);
  const radioOptions = createChannelBindingOptions(espAccounts, defaultAccount);

  const { isLoading, mutate } = useUpdateChannelMutation({
    onSuccess: () => {
      const channelName = getValues('channelName');
      onClose();
      dispatch(addPositiveToast(<PositiveToastMessage channelName={channelName} />));
      refetchChannels();
    },
    onError: (e: unknown) => {
      const { response } = e as ApiError;
      const errorText = `${UPDATE_CHANNEL_TEXT.ERROR_MESSAGE} ${response?.data}.`;
      onClose();
      dispatch(addNegativeToastWithErrorId(errorText, 'useUpdateChannelMutation', e));
    },
  });

  const onSubmit = (values: UpdateChannelFormState) => {
    const convertedEspBindings = convertToEspBindings(values.channelBindings || []);
    const name = values.channelName.trim();
    const payload = {
      channelType,
      espBindings: values.bindingType === BindingTypes.DEFAULT ? [] : convertedEspBindings,
      id,
      name,
    };
    mutate(payload);
  };

  return (
    <Modal
      id={UPDATE_CHANNEL_TEXT.MODAL_ID}
      isModalOpen={true}
      onClose={onClose}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader autoFocus={false}>Edit {channel.name}</ModalHeader>
      <ModalBody>
        <FormField
          labelText={CHANNEL_FORM_FIELDS_TEXT[CHANNEL_NAME].LABEL}
          hasError={Boolean(channelNameError?.message)}
          errorText={`${channelNameError?.message}`}
        >
          <InputField
            autoFocus
            data-test="channel-name-input"
            id={CHANNEL_FORM_FIELDS_TEXT[CHANNEL_NAME].ID}
            name={CHANNEL_NAME}
          />
        </FormField>
        {isEmail && (
          <FormField
            data-test="binding-type-options"
            errorText={bindingTypeError?.message}
            hasError={!!bindingTypeError?.message}
            labelText={<SplitInputLabel />}
          >
            <RadioListField name={BINDING_TYPE} options={radioOptions} />
          </FormField>
        )}
        {isEmail && isCustom && <SendingPlatformAccounts />}
      </ModalBody>
      <ModalFooter onClose={onClose}>
        <PrimaryButton
          data-test="update-channel-button"
          id={UPDATE_CHANNEL_TEXT.SAVE_BUTTON_ID}
          isDisabled={!isValid && isSubmitted}
          onClick={handleSubmit(onSubmit)}
          isLoading={isLoading}
          size={BUTTON_SIZES.LARGE}
        >
          {UPDATE_CHANNEL_TEXT.SAVE_BUTTON_LABEL}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};

export const UpdateChannelModal = ({ channel, onClose }: UpdateModalProps) => {
  return (
    <UpdateChannelFormContext channel={channel}>
      <UpdateChannel channel={channel} onClose={onClose} />
    </UpdateChannelFormContext>
  );
};
